﻿<script setup lang="ts">
    import { startDuplicateMerge, listDuplicates, DuplicateContact, mergeSingleContact } from '@cwd/Account/DataManagement.access';
    import { inject, onMounted, reactive } from 'vue';
    import { vPopconfirm } from '@shared/utils/directives/popovers';
    import List from '@shared/components/List.vue';
    import BSFormSearch from '@shared/components/BSFormSearch.vue';
    import { $Resources } from '../lib/main';
    import Loading from '@shared/components/Loading.vue';
    import { ScopeAccess, vRequireScopeParams } from '@shared/directives/Scope.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { Permissions } from '@shared/Root/Permissions';
    import { debounce } from '@shared/utils';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);

    const state = reactive<{
        duplicates: DuplicateContact[];
        numberOfDuplicates: number | null;
        currentTotal: number | null;
        testMode: boolean;
        searchArg: string;
        step: number;
        confirm: boolean;
        loading: boolean;
        mergeStarted: boolean;
        hasScope: boolean;
    }>({
        duplicates: [],
        currentTotal: null,
        numberOfDuplicates: null,
        testMode: false,
        searchArg: '',
        step: 1,
        confirm: false,
        loading: false,
        mergeStarted: false,
        hasScope: false,
    });

    onMounted(async () => {
        const requireScopeParams: vRequireScopeParams = {
            Feature: 'Account',
            Permission: Permissions.Write,
        };
        state.hasScope = scopeAccess.hasScope(requireScopeParams);

        loadDuplicates();
    });

    const reset = async () => {
        state.duplicates = [];
        await loadDuplicates();
    };

    const search = () => {
        reset();
    };

    const resetSearch = () => {
        state.searchArg = '';
        reset();
    };

    const loadDuplicates = async () => {
        if (state.loading || !state.hasScope) return;

        state.loading = true;

        return await listDuplicates(state.duplicates.length, state.searchArg).then((response) => {
            state.loading = false;

            if (state.numberOfDuplicates == null) state.numberOfDuplicates = response.Count;

            state.currentTotal = response.Count;
            state.duplicates = [...state.duplicates].concat(response.Results);
        });
    };

    const mergeContact = async (e: any, i: any, item: any) => {
        // return console.log('mergeContact')
        const contact = item as DuplicateContact;
        return mergeSingleContact(contact).then(() => {
            console.log(contact, item);
            state.duplicates = state.duplicates.filter((dup) => dup.Email != contact.Email);
        });
    };

    const mergeAll = async () => {
        state.mergeStarted = true;
        return startDuplicateMerge();
    };

    /*
	let currentTimer = 0

	const debounce = async (searchArg: string) => {
		state.searchArg = searchArg
		console.log('searcharg', searchArg)
		clearTimeout(currentTimer)
		currentTimer = window.setTimeout(() => {
			console.log("'timeout", searchArg)
			return loadDuplicates()
		}, 600)
	}
    */
    const filterUpdated = (newVal: string) => {
        debounce(async () => {
            state.searchArg = newVal;
            await reset();
        });
    };
    defineExpose({ state });
</script>
<script lang="ts">
    export default {
        name: 'MergeContactManager',
    };
</script>

<template>
    <div class="max-700">
        <div v-if="!state.testMode">
            <p>
                {{ $Resources.MergeContactManagerFeatureDescription }}
            </p>
            <h4 class="m-lg-t">{{ $Resources.MergeContactManagerWhatIsADuplicateHeader }}</h4>
            <p>
                {{ $Resources.MergeContactManagerWhatIsADuplicateBody }}
            </p>

            <h4 class="m-lg-t">{{ $Resources.MergeContactManagerMergeInformationHeader }}</h4>
            <ul v-html="$Resources.MergeContactManagerMergeInformationList"></ul>

            <div class="alert alert-warning alert-fixed-i m-lg-t">
                <p>
                    <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                    <strong>{{ $Resources.MergeContactManagerInformationLossWarningHeader }}</strong>
                    {{ $Resources.MergeContactManagerInformationLossWarningBody }}
                </p>
            </div>

            <div class="panel panel-default">
                <div class="panel-body">
                    <p class="c-light fw-600 m-xs-b">{{ $Resources.MergeContactManagerNumberOfDuplicates }}</p>
                    <p class="h1 strong m-0-t m-0-b">
                        <span>{{ state.numberOfDuplicates }}</span>
                    </p>
                </div>
            </div>

            <Loading :show="state.loading"></Loading>

            <div class="form-group m-xl-t" v-if="state.step == 1">
                <button type="button" class="btn btn-default" @click="state.step++">
                    {{ $Resources.MergeContactManagerConfirmToContinueBtnText }}
                    <i class="fa fa-chevron-right" aria-hidden="true"></i>
                </button>
            </div>
        </div>
        <div v-if="state.step > 1 && !state.testMode">
            <div v-if="state.numberOfDuplicates && state.numberOfDuplicates > 0">
                <div class="form-group">
                    <button
                        type="button"
                        class="btn btn-default"
                        :disabled="state.mergeStarted"
                        v-if="!state.testMode"
                        @click="state.testMode = !state.testMode">
                        {{ $Resources.MergeContactManagerShowDuplicateListBtn }}
                    </button>
                </div>

                <div class="alert alert-danger alert-fixed-i m-sm-b">
                    <p>
                        <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                        <strong>{{ $Resources.MergeContactManagerConfirmWarning }}</strong>
                        {{ $Resources.MergeContactManagerConfirmWarningPart1 }}
                        <strong>{{ state.numberOfDuplicates }}</strong>
                        {{ $Resources.MergeContactManagerConfirmWarningPart2 }}
                    </p>

                    <div class="form-group">
                        <div class="checkbox slide-toggle">
                            <label>
                                <input type="checkbox" v-model="state.confirm" class="form-check-inline" />
                                <span class="toggle"></span>
                                {{ $Resources.MergeContactManagerConfirmToggleLabel }}
                            </label>
                        </div>
                    </div>
                </div>

                <div class="form-group">
                    <button class="btn btn-danger btn-block" :disabled="!state.confirm" @click="mergeAll">
                        {{ $Resources.MergeContactManagerExecuteButton }}
                    </button>
                </div>
                <div v-if="state.mergeStarted === true">
                    <div class="alert alert-success alert-fixed-i m-sm-t">
                        <p>
                            <i class="fa fa-check" aria-hidden="true"></i>
                            <strong>{{ $Resources.MergeContactManagerMergeStartedHeading }}</strong>
                            {{ $Resources.MergeContactManagerMergeStartedBody }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-if="state.testMode">
        <p class="d-flex justify-content-between">
            <a href="#" v-if="state.testMode" @click="state.testMode = !state.testMode">{{ $Resources.MergeContactManagerGoBack }}</a>
            <span>
                {{ $Resources.MergeContactManagerPagingShow }} {{ state.duplicates.length }} {{ $Resources.MergeContactManagerPagingOf }}
                {{ state.currentTotal }}
            </span>
        </p>

        <List
            itemId="Email"
            :serverSideFilter="true"
            :allLoaded="state.duplicates.length === state.currentTotal"
            :loading="state.loading"
            :items="state.duplicates"
            @loadMore="loadDuplicates"
            @update:filter="(newVal: string) => filterUpdated(newVal)">
            <template #item="{ item }">
                <div class="flex-1 d-flex gap-lg flex-wrap align-items-baseline">
                    <h4 class="m-0-t m-xs-b flex-1">
                        {{ item.Email }}
                    </h4>
                    <p>
                        {{ $Resources.MergeContactManagerListDuplicateCount }}
                        <strong>{{ item.Contacts.length }}</strong>
                    </p>
                    <div class="m-sm-b">
                        <button
                            class="btn btn-sm btn-warning"
                            v-popconfirm="{
                                heading: $Resources.MergeContactManagerSingleConfirmHeading,
                                body: $Resources.MergeContactManagerSingleConfirmBody,
                                callback: (e, i) => mergeContact(e, i, item),

                                placement: 'top',
                            }">
                            {{ $Resources.MergeContactManagerMergeSingleButton }}
                        </button>
                    </div>
                </div>
            </template>
        </List>
    </div>
</template>

<style scoped></style>
inject, import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys'
